import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AuthUser, fetchMe, verifyGoogleCredential, logout as apiLogout } from "../lib/api";

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  loginWithGoogle: (credential: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<AuthUser>) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthContext.Provider");
  return ctx;
}

export function useAuthState(): AuthContextValue {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    fetchMe().then((u) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  const loginWithGoogle = useCallback(async (credential: string) => {
    try {
      const u = await verifyGoogleCredential(credential);
      setUser(u);
    } catch {
      setUser(null); // Ensure login button reappears on failure
    }
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
  }, []);

  const updateUser = useCallback((updates: Partial<AuthUser>) => {
    setUser((prev) => prev ? { ...prev, ...updates } : null);
  }, []);

  return { user, loading, loginWithGoogle, logout, updateUser };
}
